import { Link } from "react-router-dom";
import { ShieldCheck, XCircle, FileCheck, AlertTriangle, Info, Database } from "lucide-react";
import { SCORE_ITEMS, formatDateTime } from "@/lib/testFormat";

/** result: { result, overallScore, scores, failures, warnings, breakdown, usedReference } */
export function TestResultPanel({ result, testId, isPreview = false, certificateUrl }) {
  if (!result) return null;
  const isPassed = result.result === "PASS";

  return (
    <div
      data-testid="test-result-section"
      className={`border rounded-2xl p-6 sm:p-8 mb-8 ${
        isPassed ? "bg-emerald-500/5 border-emerald-500/20" : "bg-red-500/5 border-red-500/20"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-5 text-[11px] font-mono">
        {isPreview ? (
          <span data-testid="result-preview-badge" className="px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
            PREVIEW · not saved yet
          </span>
        ) : (
          <span data-testid="result-saved-badge" className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            SAVED · {formatDateTime(result.evaluatedAt)}
          </span>
        )}
        <span className="px-2 py-0.5 rounded bg-zinc-800 text-zinc-400">
          {result.usedReference ? "Scored against reference profile" : "Scored with default limits (no reference profile)"}
        </span>
      </div>

      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <ResultBadge isPassed={isPassed} overallScore={result.overallScore} />
        <ScoreBreakdown scores={result.scores || {}} />
      </div>

      {(result.failures?.length > 0 || result.warnings?.length > 0) && (
        <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-4">
          {result.failures?.length > 0 && (
            <div data-testid="result-failures">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-red-400 mb-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Reasons
              </div>
              <ul className="space-y-1">
                {result.failures.map((f) => (
                  <li key={f} className="text-sm text-zinc-300">• {f}</li>
                ))}
              </ul>
            </div>
          )}
          {result.warnings?.length > 0 && (
            <div data-testid="result-warnings">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-yellow-400 mb-2">
                <Info className="w-3.5 h-3.5" /> Warnings
              </div>
              <ul className="space-y-1">
                {result.warnings.map((w) => (
                  <li key={w} className="text-sm text-zinc-400">• {w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {result.breakdown && <MetricBreakdown breakdown={result.breakdown} />}

      {isPassed && !isPreview && (
        <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap gap-3 justify-end">
          {certificateUrl && (
            <a
              href={certificateUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-certificate-pdf"
              className="inline-flex items-center gap-2 bg-zinc-800 text-white border border-white/10 font-bold py-2.5 px-5 rounded-lg hover:bg-zinc-700 transition-colors"
            >
              Download PDF
            </a>
          )}
          <Link
            to={`/certificate/${testId}`}
            data-testid="view-certificate-btn"
            className="inline-flex items-center gap-2 bg-yellow-500 text-black font-bold py-2.5 px-5 rounded-lg hover:bg-yellow-400 transition-colors"
          >
            <FileCheck className="w-4 h-4" />
            View Certificate
          </Link>
        </div>
      )}
    </div>
  );
}

export function ReferenceResultPanel({ metrics }) {
  return (
    <div data-testid="reference-result-section" className="border border-sky-500/20 bg-sky-500/5 rounded-2xl p-6 sm:p-8 mb-8">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
          <Database className="w-7 h-7 text-sky-400" />
        </div>
        <div>
          <div data-testid="test-result-status" className="text-2xl font-heading font-black text-sky-400">REFERENCE DATA</div>
          <p className="text-sm text-zinc-400 mt-1">
            Hardware baseline only, not graded pass/fail. Used to build the reference profile that candidates are scored against.
          </p>
        </div>
      </div>
      {metrics && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            ["Duration", `${metrics.durationSec} s`],
            ["Turns", (metrics.detectedSequence || []).map((d) => d[0]).join("-") || "none"],
            ["Jerk RMS", `${metrics.jerkRms} g/s`],
            ["Roll RMS", `${metrics.rollRms} °/s`],
          ].map(([l, v]) => (
            <div key={l} className="bg-white/5 rounded-lg p-3">
              <div className="text-[10px] font-mono uppercase text-zinc-500">{l}</div>
              <div className="text-sm font-mono text-white mt-1">{v}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultBadge({ isPassed, overallScore }) {
  const Icon = isPassed ? ShieldCheck : XCircle;
  const colorClass = isPassed ? "text-emerald-400" : "text-red-400";
  const bgClass = isPassed ? "bg-emerald-500/10 border-emerald-500/20" : "bg-red-500/10 border-red-500/20";
  return (
    <div className="flex items-center gap-4">
      <div className={`w-16 h-16 rounded-2xl ${bgClass} border flex items-center justify-center`}>
        <Icon className={`w-8 h-8 ${colorClass}`} />
      </div>
      <div>
        <div data-testid="test-result-status" className={`text-3xl font-heading font-black ${colorClass}`}>
          {isPassed ? "PASS" : "FAIL"}
        </div>
        <p className="text-sm text-zinc-400 mt-1">
          Overall Score: <span data-testid="test-result-score" className="text-white font-mono font-bold">{overallScore}/100</span>
        </p>
      </div>
    </div>
  );
}

function ScoreBreakdown({ scores }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 w-full lg:w-auto">
      {SCORE_ITEMS.map((item) => {
        const score = scores[item.key];
        const Icon = item.icon;
        return (
          <div key={item.key} data-testid={`score-${item.key}`} className="bg-white/5 rounded-lg p-3 text-center min-w-[96px]">
            <Icon className="w-4 h-4 text-zinc-500 mx-auto mb-1" />
            <div className="text-xs font-mono text-zinc-500 mb-1">{item.label} · {item.weight}%</div>
            <div className={`text-xl font-mono font-bold ${score >= 70 ? "text-emerald-400" : "text-red-400"}`}>
              {Number.isFinite(score) ? Math.round(score) : "—"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MetricBreakdown({ breakdown }) {
  const rows = [...(breakdown.smoothness || []), ...(breakdown.stability || [])];
  if (!rows.length) return null;
  return (
    <div data-testid="metric-breakdown" className="mt-6 pt-6 border-t border-white/10 overflow-x-auto">
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">Measured vs limit</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] font-mono uppercase text-zinc-500 text-left">
            <th className="py-1 pr-4 font-normal">Metric</th>
            <th className="py-1 pr-4 font-normal">Value</th>
            <th className="py-1 pr-4 font-normal">Full marks ≤</th>
            <th className="py-1 pr-4 font-normal">Limit</th>
            <th className="py-1 pr-4 font-normal">Source</th>
            <th className="py-1 font-normal text-right">Score</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-white/5">
              <td className="py-1.5 pr-4 text-zinc-300">{r.label}</td>
              <td className="py-1.5 pr-4 font-mono text-white">{r.value}</td>
              <td className="py-1.5 pr-4 font-mono text-zinc-400">{Number(r.good).toFixed(3)}</td>
              <td className="py-1.5 pr-4 font-mono text-zinc-400">{Number(r.limit).toFixed(3)}</td>
              <td className="py-1.5 pr-4 text-xs text-zinc-500">{r.source}</td>
              <td className={`py-1.5 font-mono text-right ${r.score >= 70 ? "text-emerald-400" : "text-red-400"}`}>{r.score}</td>
            </tr>
          ))}
          {breakdown.time && (
            <tr className="border-t border-white/5">
              <td className="py-1.5 pr-4 text-zinc-300">Duration window</td>
              <td className="py-1.5 pr-4 font-mono text-white">—</td>
              <td className="py-1.5 pr-4 font-mono text-zinc-400" colSpan={2}>{breakdown.time.minSec}s – {breakdown.time.maxSec}s</td>
              <td className="py-1.5 pr-4 text-xs text-zinc-500">{breakdown.time.source}</td>
              <td className={`py-1.5 font-mono text-right ${breakdown.time.score >= 70 ? "text-emerald-400" : "text-red-400"}`}>{breakdown.time.score}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
